import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DropdownChangeEvent } from 'primeng/dropdown';
import { AdvancedFilterProject } from 'src/app/shared/models/advanced-filter';
import { Catalog } from 'src/app/shared/models/echo/catalog';
import { Formulation } from 'src/app/shared/models/formulation';
import { Project } from 'src/app/shared/models/project';
import { EEACopyProjectDialogComponent } from '../shared/eea-copy-project-dialog/eea-copy-project-dialog.component';
import { EEAGapApiService } from '../../eea-gap/eea-gap.api.service';
import { firstValueFrom, take } from 'rxjs';
import { EndpointDatasetService } from 'src/app/shared/components/endpoint-dataset/endpoint-dataset.service';
import { SelectedProjectApiService } from 'src/app/shared/components/selected-project/selected-project.api.service';
import Swal from 'sweetalert2';
import { AutoCompleteCompleteEvent, AutoCompleteDropdownClickEvent, AutoCompleteOnSelectEvent } from 'primeng/autocomplete';
import { FormulationApiService } from 'src/app/shared/services/echo/formulation.api.service';
import { UserLogicService } from 'src/app/shared/services/user.logic.service';
import { Constants } from 'src/app/shared/utils/constants';
import { HHRAConstants } from 'src/app/human-health-risk-assessments/hhra-constants';
import { PersonalProtectionEquipmentSchemeSharedApiService } from 'src/app/shared/services/personal-protection-equipment-scheme-shared.api.service';
import { SetupLogicService } from 'src/app/shared/services/setup.logic.service';
import { PWCApiService } from 'src/app/shared/services/pwc.api.service';
import { DuplicatedCombinationLogicService } from 'src/app/shared/services/duplicated-combination.logic.service';

@Component({
  selector: 'app-eea-setup-inputs',
  templateUrl: './eea-setup-inputs.component.html',
  styleUrls: ['./eea-setup-inputs.component.css']
})
export class EEASetupInputsComponent implements OnChanges {

  @Input() filteredFormulations: Formulation[] = [];
  @Input() geographies: Catalog[] = [];
  @Input() selectedProject?: Project;
  @Input() currentUserName: string = "";
  @Input() isProjectOwnershipValid: boolean = false;
  @Input() isHHRA: boolean = false;
  @Input() isInverseModeling: boolean = false;

  createdBy: string = "";
  selectedGeography?: Catalog;
  selectedFormulation?: Formulation;
  selectedProductCategory?: Catalog;
  productCategoryOptions = HHRAConstants.HHRA_OPEX_PRODUCT_CATEGORY;

  @Output() onformulationSelectionChangeEvent = new EventEmitter<Formulation>();
  @Output() onQueryFilterChangeEvent = new EventEmitter<string>();
  @Output() isSetupValidEvent = new EventEmitter<boolean>();
  @Output() onGeographySelectionChangeEvent = new EventEmitter<void>();

  loadingFormulations: boolean = true;
  loadingGeographies: boolean = true;
  isProjectNameValid: boolean = true;
  isProjectNameDuplicated: boolean = false;
  isFormulationValid: boolean = true;
  isGeographyValid: boolean = true;
  loading: boolean = false;
  isArchiveButtonDisabled: boolean = true;
  showOPEXFormulationType: boolean = true;
  isEEA: boolean = false;
  showDuplicatedCombinationDialog: boolean = false;
  duplicatedProjects: AdvancedFilterProject[] = [];
  duplicatedFormulationName: string = "";
  duplicatedGeographyName: string = "";
  @ViewChild('appDialog') copyprojectDialog!: EEACopyProjectDialogComponent;

  private setupLogicService = inject(SetupLogicService);
  private duplicatedCombinationService = inject(DuplicatedCombinationLogicService);
  private destroyRef = inject(DestroyRef);
  private isFormulationCleared: boolean = false;

  constructor(private gapGWApiService: EEAGapApiService,
    public dataSetService: EndpointDatasetService,
    private userService: UserLogicService,
    public projectApiService: SelectedProjectApiService,
    private formulationAPIService: FormulationApiService,
    private personalProtectionEquipmentSchemeSharedApiService: PersonalProtectionEquipmentSchemeSharedApiService,
    private pwcApiService: PWCApiService) {
    this.duplicatedCombinationService.duplicatedCombination$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ projects }) => this.showDuplicatedCombination(projects));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedProject']) {
      this.createdBy = this.selectedProject ? this.selectedProject.createdBy ? this.selectedProject.createdBy : "" : this.currentUserName;
      this.validateSelectedProject();
      this.initSelectedFormulation();
      this.isFormulationCleared = false;
      this.closeDuplicatedCombination();
      this.duplicatedCombinationService.reset();
    }
    this.loadingGeographies = changes['geographies']?.currentValue.length == 0;
    if (!this.loadingGeographies) this.initSelectedGeography();
    this.initProductCategory();
    this.setArchiveButtonStatus();
    this.setIsEEA();
  }

  initSelectedFormulation() {
    this.selectedFormulation = this.filteredFormulations.find(f => f.formulationPK == this.selectedProject?.formulationPk);
    this.updateSelectedFormulation(this.selectedFormulation);
  }

  initSelectedGeography() {
    this.selectedGeography = this.selectedProject?.regionPk ?
      this.geographies.find(g => g.key == this.selectedProject?.regionPk && g.source == 'Region') :
      this.selectedProject?.countryPk ?
        this.geographies.find(g => g.key == this.selectedProject?.countryPk && g.source == 'Country')
        : undefined;
    if (this.selectedGeography)
      this.updateSelectedGeography(this.selectedGeography);
  }

  initProductCategory() {
    const categoryName = this.selectedProject?.OPEXProductCategory ?? HHRAConstants.HHRA_OPEX_PRODUCT_CATEGORY_DEFAULT;
    const categoryOption = HHRAConstants.HHRA_OPEX_PRODUCT_CATEGORY.find(option => option.name === categoryName);
    const categoryKey = categoryOption ? categoryOption.key : 0;
    this.selectedProductCategory = { name: categoryName, key: categoryKey, active: true };
    if (this.selectedProject && !this.selectedProject.OPEXProductCategory) {
      this.selectedProject.OPEXProductCategory = HHRAConstants.HHRA_OPEX_PRODUCT_CATEGORY_DEFAULT;
    }
  }

  onQueryFilterEvent({ query }: AutoCompleteCompleteEvent) {
    this.onQueryFilterChangeEvent.emit(query);
  }

  formulationSelectionChanged({ value }: AutoCompleteOnSelectEvent) {
    this.isFormulationValid = value != null;
    this.updateSelectedFormulation(value);
    this.isFormulationCleared = false;
    this.checkDuplicatedCombination();
  }

  formulationOnClear(event: any) {
    this.updateSelectedFormulation();
    this.isFormulationCleared = true;
    this.closeDuplicatedCombination();
  }

  formulationDropdownClicked({ query }: AutoCompleteDropdownClickEvent) {
    if (query) this.onQueryFilterChangeEvent.emit(query);
    if (!query) this.onQueryFilterChangeEvent.emit('1');
  }

  updateSelectedFormulation(formulation?: Formulation) {
    if (formulation?.formulationPK && this.selectedProject) {
      this.selectedProject.formulationPk = formulation.formulationPK;
      this.selectedProject.formulation = formulation.tradeName;

    }
    this.onformulationSelectionChangeEvent.emit(formulation);
    this.validateSetupInfoInProject();
  }

  geographySelectionChanged({ value }: DropdownChangeEvent) {
    this.isGeographyValid = value != null;
    const geography: Catalog = value as Catalog;
    this.updateSelectedGeography(geography);
    this.onGeographySelectionChangeEvent.emit();
    this.checkDuplicatedCombination();
  }

  productCategorySelectionChanged({ value }: DropdownChangeEvent) {
    const productCategory: Catalog = value as Catalog;
    this.selectedProductCategory = productCategory;
    if (this.selectedProject) {
      this.selectedProject.OPEXProductCategory = productCategory?.name ?? HHRAConstants.HHRA_OPEX_PRODUCT_CATEGORY_DEFAULT;
    }
  }

  updateSelectedGeography(geography: Catalog) {
    const geographiesToFilter = [Constants.CROP_GEOGRAPHIES.EUROPE_UNION, Constants.CROP_GEOGRAPHIES.UK, Constants.CROP_GEOGRAPHIES.USA];

    if (!geography && this.selectedProject) {
      this.selectedProject.regionPk = undefined
      this.selectedProject.countryPk = undefined;
      this.selectedProject.geography = undefined;
    }

    if (geography?.source == 'Region' && this.selectedProject) {
      this.selectedProject.regionPk = geography.key;
      this.selectedProject.countryPk = undefined;
      this.selectedProject.geography = this.geographies.filter((x: Catalog) => geographiesToFilter.includes(x.name ?? ''))?.find((x: Catalog) => geography.key === x.key)?.name;
    }

    if (geography?.source == 'Country' && this.selectedProject) {
      this.selectedProject.countryPk = geography.key;
      this.selectedProject.regionPk = undefined
      this.selectedProject.geography = this.geographies.filter((x: Catalog) => geographiesToFilter.includes(x.name ?? ''))?.find((x: Catalog) => geography.key === x.key)?.name;
    }

    this.setupLogicService.selectedGeographyKey.set(geography);

    this.showOPEXFormulationType = geography?.name == Constants.CROP_GEOGRAPHIES.EUROPE_UNION;
    this.validateSetupInfoInProject();
  }

  async projectNameChanged(value: string) {
    this.isProjectNameValid = value.length > 0;
    this.isProjectNameDuplicated = false;
    await firstValueFrom(this.projectApiService.projects).then(projects => {
      if (projects.find(p => p.name == value && p.name != '' && p.projectPk != this.selectedProject?.projectPk)) {
        this.isProjectNameValid = false;
        this.isProjectNameDuplicated = true;
      }
    });
    if (this.selectedProject)
      this.selectedProject.name = value;

    this.validateSetupInfoInProject();
  }

  projectDescriptionChanged(value: string) {
    if (this.selectedProject)
      this.selectedProject.description = value;
  }

  validateSelectedProject() {
    this.isProjectNameValid = this.selectedProject?.name ? this.selectedProject?.name.length > 0 : false;
    this.isFormulationValid = this.selectedProject?.formulationPk != null && this.selectedProject?.formulationPk != 0;
    this.isGeographyValid = this.selectedProject?.regionPk != null || this.selectedProject?.countryPk != null;
  }

  validateSetupInfoInProject() {
    if (this.selectedProject?.name && (this.selectedProject.regionPk || this.selectedProject.countryPk) && this.selectedProject?.formulationPk && this.isProjectNameValid) {
      this.isSetupValidEvent.emit(true);
      return;
    }
    this.isSetupValidEvent.emit(false);
  }

  copyProject() {
    if (this.selectedProject?.name != "")
      this.copyprojectDialog.showDialog(true, this.selectedProject?.projectPk);
  }

  onSaveDataProject(event: any) {
    const projectPkCopy = event.projectPkCopy;
    const datasetList = this.addCreatedByToDataSets(event.datasetList);

    this.loading = true;
    if (this.selectedProject?.projectPk == -1) {
      this.onCopyProject(projectPkCopy, -1, this.selectedProject?.name, datasetList);
    }
    else if (projectPkCopy != 0 && this.selectedProject?.projectPk != -1) {
      Swal.fire({
        text: 'Do you want to overwrite the project data?',
        showCancelButton: true,
        confirmButtonColor: '#0069be',
        confirmButtonText: 'Yes',
        cancelButtonText: 'No',
        icon: 'warning'
      }).then((result) => {
        if (result.value) {
          this.onCopyProject(projectPkCopy, this.selectedProject?.projectPk, '', datasetList);
        }
        else {
          this.loading = false;
        }
      });
    }
  }

  onCopyProject(projectPkCopy: number, projectPk?: number, nameProject?: string, datasetList?: any[]) {
    this.projectApiService.getAndCopyProject(projectPkCopy, projectPk, nameProject, this.currentUserName).pipe(take(1)).subscribe(async projectNew => {
      await firstValueFrom(this.formulationAPIService.getFormulationByPk(projectNew.formulationPk!).pipe(take(1))).then((formulation: Formulation) => {
        projectNew.formulation = formulation.tradeName;
      });
      this.gapGWApiService.getAndCopyApplicationSchemesByProjectAndCompartment(projectPkCopy, projectNew.projectPk)
        .pipe(take(1)).subscribe(() => {
          this.dataSetService.getAndCopyCoreDatasetsByProject(projectNew, datasetList)
            .subscribe((data: any) => {
              this.pwcApiService.copyDataSet(data).subscribe(() => {
                const copyModels$ = (data?.length > 0)
                  ? this.projectApiService.copyModelsToBeRun(projectPkCopy, projectNew.projectPk, data).pipe(take(1))
                  : null;
                if (copyModels$) {
                  copyModels$.subscribe({ next: () => this.finishProjectCopy(projectPkCopy, projectNew), error: () => this.finishProjectCopy(projectPkCopy, projectNew) });
                } else {
                  this.finishProjectCopy(projectPkCopy, projectNew);
                }
              });
            });
        });
    });
  }

  private finishProjectCopy(projectPkCopy: number, projectNew: Project): void {
    this.personalProtectionEquipmentSchemeSharedApiService.getAndCopyPersonalProtectionEquipmentSchemes(projectPkCopy, projectNew.projectPk)
      .subscribe(() => {
        this.projectApiService.setSelectedProject(projectNew);
        this.loading = false;
        Swal.fire({
          text: 'Data Saved Successfully',
          confirmButtonColor: '#0069be',
          confirmButtonText: 'Ok',
        });
      });
  }

  archiveProject() {
    if (this.selectedProject?.name != undefined && this.selectedProject?.name != '')
      if (this.selectedProject?.name != undefined && this.selectedProject?.name != '')
        Swal.fire({
          text: 'Do you want to archive the current project?',
          showCancelButton: true,
          confirmButtonColor: '#0069be',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          icon: 'warning'
        }).then((result) => {
          if (result.value) {
            if (this.selectedProject) {
              this.selectedProject.active = false;
              this.projectApiService.saveProject().subscribe(data => {
                this.loading = false;
                Swal.fire({
                  text: 'Project archived successfully',
                  confirmButtonColor: '#0069be',
                  confirmButtonText: 'Ok',
                  icon: 'success'
                }).then((result) => {
                  if (result.value)
                    location.reload();
                });
              });
            }
          }
          else {
            this.loading = false;
          }
        });
  }

  setArchiveButtonStatus() {
    this.isArchiveButtonDisabled = !this.selectedProject
      || this.userService.profile.displayName !== this.selectedProject?.createdBy
      || !this.userService.currentUserCanRunModels(this.userService.selectedRole?.role);
  }

  setIsEEA() {
    this.isEEA = !this.isHHRA && !this.isInverseModeling;
  }

  addCreatedByToDataSets(datasetList?: any[]): any {
    if (datasetList) {
      datasetList.forEach(dataset => {
        dataset.createdBy = this.userService.profile.displayName;
      });
    }
    return datasetList;
  }

  public onClearGeography(): void {
    this.setupLogicService.selectedGeographyKey.set(undefined);
    this.onGeographySelectionChangeEvent.emit();
    this.closeDuplicatedCombination();
  }

  private checkDuplicatedCombination(): void {
    if (this.isFormulationCleared) return;
    this.duplicatedCombinationService.notifyCombinationChanged(this.selectedProject);
  }

  private showDuplicatedCombination(projects: AdvancedFilterProject[]): void {
    this.duplicatedProjects = projects;
    this.duplicatedFormulationName = this.selectedProject?.formulation ?? "";
    this.duplicatedGeographyName = this.setupLogicService.selectedGeographyKey()?.name ?? "";
    this.showDuplicatedCombinationDialog = true;
  }

  public closeDuplicatedCombination(): void {
    this.showDuplicatedCombinationDialog = false;
  }

  public openDuplicatedProject(projectPk: number): void {
    if (projectPk === this.selectedProject?.projectPk) return;
    this.closeDuplicatedCombination();
    this.duplicatedCombinationService.reset();
    this.projectApiService.requestProjectSelection(projectPk);
  }

}
